// @flow

import { applySpec, compose, invertObj, map, pathOr, pipe, prop } from "ramda";

import {
  FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
  FORMA_VISUALIZACAO_SERIES_POR_DATAS,
} from "./constantes";

export type GridStateSaveTypes = "SORT" | "FILTER" | "GROUP";

const filterTypes = {
  TEXTO: "text",
  NUMERICO: "number",
  DATA: "date",
  COMPOSICAO: "composition",
  CONJUNTO: "aggregation",
};

const filterTypeOperators = {
  EQUALS: "equals",
  NOT_EQUAL: "notEqual",
  CONTAINS: "contains",
  NOT_CONTAINS: "notContains",
  STARTS_WITH: "startsWith",
  ENDS_WITH: "endsWith",
  LESS_THAN: "lessThan",
  LESS_THAN_OR_EQUAL: "lessThanOrEqual",
  GREATER_THAN: "greaterThan",
  GREATER_THAN_OR_EQUAL: "greaterThanOrEqual",
  IN_RANGE: "inRange",
  EMPTY: "empty",
};

const aggregationTypes = {
  SUM: "sum",
  MIN: "min",
  MAX: "max",
  COUNT: "count",
  AVG: "avg",
  FIRST: "first",
  LAST: "last",
};

const parseFilter = filter => {
  if (filter.tipoComposicao != null) {
    return {
      column: filter.column,
      operator: filter.tipoComposicao,
      filters: filter.filtro.map(parseFilter),
    };
  }

  switch (filter.tipoFiltro) {
    case "TEXTO":
      return {
        column: filter.coluna,
        filterType: filterTypes[filter.tipoFiltro],
        type: filterTypeOperators[filter.tipo],
        filter: filter.filtro,
      };

    case "NUMERICO":
      return {
        column: filter.coluna,
        filterType: filterTypes[filter.tipoFiltro],
        type: filterTypeOperators[filter.tipo],
        filter: filter.filtro,
      };

    case "DATA": {
      const result = {
        column: filter.coluna,
        filterType: filterTypes[filter.tipoFiltro],
        type: filterTypeOperators[filter.tipo],
      };

      switch (filter.tipo) {
        case "IN_RANGE":
          return {
            ...result,
            dateFrom: filter.filtro,
            dateTo: filter.filtroMaior,
          };

        default:
          return {
            ...result,
            dateFrom: filter.filtro,
          };
      }
    }

    default: {
      console.error("Tipo de filtro não suportado", filter);
      throw new Error("Tipo de filtro não suportado");
    }
  }
};

const serializeFilter = filter => {
  if (filter.filterType === "COMPOSITION") {
    const coluna = filter.column;
    return {
      coluna,
      tipoFiltro: "COMPOSICAO",
      tipoComposicao: filter.operator,
      filtros: filter.filters
        .map(serializeFilter)
        .map(filter => ({ ...filter, coluna })),
    };
  }

  const tipoFiltro = invertObj(filterTypes)[filter.filterType];
  const tipo = invertObj(filterTypeOperators)[filter.type];

  if (!tipoFiltro) console.error("Tipo filtro não suportado", filter);
  if (!tipo) console.error("Tipo de operador de filtro não suportado", filter);

  switch (filter.filterType) {
    case "date": {
      const baseFilter = {
        coluna: filter.column,
        tipoFiltro,
        tipo,
      };

      switch (filter.type) {
        case "inRange":
          return {
            ...baseFilter,
            filtro: filter.dateFrom,
            filtroMaior: filter.dateTo,
          };

        default:
          return {
            ...baseFilter,
            filtro: filter.dateFrom,
          };
      }
    }

    default:
      return {
        coluna: filter.column,
        tipoFiltro,
        tipo,
        filtro: filter.filter,
      };
  }
};

export const parseVisualization = applySpec({
  filter: pipe(pathOr([], ["filtros"]), map(parseFilter)),
  order: pathOr([], ["colunas"]),
  sort: pipe(
    pathOr([], ["ordenacao"]),
    map(({ coluna, tipo }) => [coluna, tipo.toLowerCase()])
  ),
  group: pathOr([], ["grupos"]),
  aggregation: pipe(
    pathOr([], ["agregacoes"]),
    map(({ coluna, funcao }) => ({
      column: coluna,
      aggFunc: aggregationTypes[funcao],
    }))
  ),
  fixedLeft: pathOr([], ["colunasFixasEsquerda"]),
  fixedRight: pathOr([], ["colunasFixasDireita"]),

  formaDeVisualizacaoRelatorioSeries: visualizacao => {
    switch (visualizacao.formaDeVisualizacaoRelatorioSeries) {
      case "POR_ATIVOS":
        return FORMA_VISUALIZACAO_SERIES_POR_ATIVOS;
      case "POR_DATAS":
        return FORMA_VISUALIZACAO_SERIES_POR_DATAS;
      default:
        return null;
    }
  },
});

export const serializeVisualization = applySpec({
  filtros: compose(map(serializeFilter), prop("filter")),
  colunas: prop("order"),
  grupos: prop("group"),
  agregacoes: compose(
    map(({ column, aggFunc }) => ({
      coluna: column,
      funcao: invertObj(aggregationTypes)[aggFunc],
    })),
    prop("aggregation")
  ),
  ordenacao: compose(
    map(([col, dir]) => ({
      coluna: col,
      tipo: dir.toUpperCase(),
    })),
    prop("sort")
  ),
  colunasFixasEsquerda: prop("fixedLeft"),
  colunasFixasDireita: prop("fixedRight"),

  formaDeVisualizacaoRelatorioSeries: visualizacao => {
    switch (visualizacao.formaDeVisualizacaoRelatorioSeries) {
      case FORMA_VISUALIZACAO_SERIES_POR_ATIVOS:
        return "POR_ATIVOS";
      case FORMA_VISUALIZACAO_SERIES_POR_DATAS:
        return "POR_DATAS";
      default:
        return null;
    }
  },
});
